import { useMemo, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import Header from "../../components/Header";
import Menu from "../../components/Menu";
import Form from "../../../../../components/shared/Form";
import Filters from "./Filters";
import Settings from "./Settings";
import UserColumnsList from "./UserColumnsList";
import { useColumns } from "./useColumns";
import { useFilters } from "./useFilters";
import { useCompanyPreferences } from "../../hooks/usePreferences";
import { usePreferenceMutation } from "../../api/usePreferenceMutation";
import { buildDefaultArray, buildDefaultFilters, flattenFilters } from "../utils";

const options: string[] = ["columns", "filters", "settings"];

function ManagerView() {
    const [option, setOption] = useState<string>("columns");
    const columns = useColumns();
    const filters = useFilters();

    const managerView = useCompanyPreferences("timesheetManagerView");
    const { mutate, isPending } = usePreferenceMutation("company", "timesheetManagerView");

    const defaultColumns = useMemo(() =>
        buildDefaultArray(columns, managerView?.columns ?? []),
        [columns, managerView]);

    const defaultFilters = useMemo(() =>
        buildDefaultFilters(flattenFilters(filters), managerView?.filters ?? []),
        [filters, managerView]);

    const formDefaults = useMemo(() => ({
        columns: defaultColumns,
        filters: defaultFilters,
        defaultFilter: managerView?.defaultFilter ?? "",
        defaultPageSize: managerView?.defaultPageSize ?? 20,
        mandatoryNoteOnEdit: managerView?.mandatoryNoteOnEdit ?? false,
        mandatoryNoteOnCreate: managerView?.mandatoryNoteOnCreate ?? false,
        hideGroup: managerView?.hideGroup ?? false,
        hideProject: managerView?.hideProject ?? false,
        hideEmployeeNumber: managerView?.hideEmployeeNumber ?? false,
        hideSummaryTooltip: managerView?.hideSummaryTooltip ?? false,
        hideMissPunchTooltip: managerView?.hideMissPunchTooltip ?? false,
        timeSheet: {
            hideDailySummary: managerView?.timeSheet?.hideDailySummary ?? false,
            hideClockDatePicker: managerView?.timeSheet?.hideClockDatePicker ?? false,
        },
    }), [defaultColumns, defaultFilters, managerView]);

    const form = useForm({
        defaultValues: formDefaults,
        // `values` is deep-compared by RHF, so a new-but-equal `formDefaults`
        // reference (e.g. from a tab switch re-render) won't reset the form.
        // It only re-syncs when the DB data actually changes, and
        // `keepDirtyValues` keeps the user's in-progress edits.
        values: managerView === undefined ? undefined : formDefaults,
        resetOptions: { keepDirtyValues: true },
    });

    const onFinish = (value: typeof formDefaults) => mutate({ ...managerView, ...value });

    // Every panel stays mounted; we only toggle visibility. Unmounting a panel
    // would unregister its <Field>s (they default to shouldUnregister: true) and
    // wipe those values from form state on every tab switch.
    const panels: { key: string; node: ReactNode }[] = [
        { key: "columns", node: <UserColumnsList /> },
        { key: "filters", node: <Filters /> },
        { key: "settings", node: <Settings /> },
    ];

    return (
        <div className="tw-p-5 tw-h-full tw-bg-white tw-rounded-lg">
            <Form className="tw-h-full" form={form} onFinish={onFinish}>
                <Header name="manager-view" isLoading={isPending} />
                <Menu
                    options={options}
                    option={option}
                    setOption={setOption}
                />

                <div className="tw-px-7" style={{ height: `calc(100% - 200px)` }}>
                    {panels.map(({ key, node }) => (
                        <div
                            key={key}
                            className="tw-h-full"
                            style={{ display: option === key ? undefined : "none" }}
                        >
                            {node}
                        </div>
                    ))}
                </div>
            </Form>
        </div>
    );
}

export default ManagerView;
